import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MedicalFormRenderer from '../../components/patient-portal/MedicalFormRenderer';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { EmptyState, ErrorBanner, Panel, PortalPage, formatPortalDate, formatPortalDateTime, statusPillClass } from './ui';

type PortalFormSummary = {
  id: string;
  status: string;
  reviewStatus: string;
  completionPercent: number;
  dueDate?: string | null;
  completedAt?: string | null;
  reviewedAt?: string | null;
  source?: string | null;
  requiresSignature?: boolean;
  canEdit?: boolean;
  canSubmit?: boolean;
  openMode?: 'portal' | 'public-link';
  openUrl?: string | null;
  template?: {
    id: string;
    name: string;
    category?: string | null;
    estimatedMinutes?: number | null;
  } | null;
};

type PortalFormDefinition = {
  version: number;
  introText?: string;
  sections: Array<{
    id: string;
    title: string;
    description?: string;
    fields: Array<{
      id: string;
      type:
        | 'text'
        | 'textarea'
        | 'number'
        | 'date'
        | 'datetime'
        | 'time'
        | 'email'
        | 'phone'
        | 'url'
        | 'select'
        | 'multiselect'
        | 'radio'
        | 'checkbox'
        | 'switch'
        | 'instruction'
        | 'rating'
        | 'scale'
        | 'signature'
        | 'file';
      label: string;
      helpText?: string;
      placeholder?: string;
      required?: boolean;
      options?: Array<{ label: string; value: string }>;
      validation?: {
        min?: number;
        max?: number;
        minLength?: number;
        maxLength?: number;
        pattern?: string;
        step?: number;
      };
      conditional?: {
        fieldId: string;
        operator: 'equals' | 'not_equals' | 'includes' | 'not_includes' | 'greater_than' | 'less_than';
        value: unknown;
      };
      layout?: {
        width?: 'full' | 'half';
      };
    }>;
  }>;
};

type PortalFormDetail = PortalFormSummary & {
  answers: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  signatureMetadata?: Record<string, unknown>;
  template: PortalFormSummary['template'] & {
    settings?: {
      allowPatientDraftSave?: boolean;
      allowPatientEditsAfterSubmit?: boolean;
      requirePatientSignature?: boolean;
      allowFileUploads?: boolean;
    };
    definition?: PortalFormDefinition;
  };
};

type PortalFormsResponse = {
  data?: PortalFormSummary[];
};

type PortalFormDetailResponse = {
  data?: PortalFormDetail;
};

function isCompletedStatus(status?: string | null) {
  return String(status || '').toUpperCase() === 'COMPLETED';
}

function badgeClass(active: boolean) {
  return active
    ? 'border-blue-600 bg-blue-600 text-white'
    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-800';
}

export default function FormsPage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { buildPortalPath, refreshNonce } = usePortalPatientData();
  const [rows, setRows] = useState<PortalFormSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<PortalFormDetail | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [listError, setListError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState<'pending' | 'completed'>('pending');

  const loadForms = useCallback(async () => {
    try {
      setLoadingList(true);
      setListError(null);
      const response = await fetchJson<PortalFormsResponse>(buildPortalPath('/portal/forms'));
      const nextRows = Array.isArray(response?.data) ? response.data : [];
      setRows(nextRows);
      setSelectedId((current) => {
        if (current && nextRows.some((item) => item.id === current)) return current;
        const pending = nextRows.find((item) => !isCompletedStatus(item.status));
        return pending?.id || nextRows[0]?.id || '';
      });
    } catch (err: any) {
      setListError(err?.message || t('forms.loadError'));
    } finally {
      setLoadingList(false);
    }
  }, [buildPortalPath, fetchJson, t]);

  const loadDetail = useCallback(
    async (formId: string) => {
      if (!formId) {
        setDetail(null);
        setAnswers({});
        return;
      }
      try {
        setLoadingDetail(true);
        setDetailError(null);
        const response = await fetchJson<PortalFormDetailResponse>(buildPortalPath(`/portal/forms/${formId}`));
        const nextDetail = response?.data || null;
        setDetail(nextDetail);
        setAnswers((nextDetail?.answers as Record<string, unknown>) || {});
      } catch (err: any) {
        setDetailError(err?.message || t('forms.detailLoadError'));
        setDetail(null);
        setAnswers({});
      } finally {
        setLoadingDetail(false);
      }
    },
    [buildPortalPath, fetchJson, t],
  );

  useEffect(() => {
    void loadForms();
  }, [loadForms, refreshNonce]);

  useEffect(() => {
    void loadDetail(selectedId);
  }, [loadDetail, selectedId]);

  const visibleRows = useMemo(
    () => rows.filter((item) => (tab === 'completed' ? isCompletedStatus(item.status) : !isCompletedStatus(item.status))),
    [rows, tab],
  );

  useEffect(() => {
    if (!selectedId && visibleRows[0]?.id) {
      setSelectedId(visibleRows[0].id);
    }
  }, [selectedId, visibleRows]);

  useEffect(() => {
    if (selectedId && !visibleRows.some((item) => item.id === selectedId)) {
      setSelectedId(visibleRows[0]?.id || '');
    }
  }, [selectedId, visibleRows]);

  async function refreshAfterAction(nextSelectedId?: string) {
    await loadForms();
    await loadDetail(nextSelectedId || selectedId);
  }

  async function handleSaveDraft() {
    if (!detail) return;
    try {
      setSaving(true);
      setActionError(null);
      setActionMessage(null);
      const response = await fetchJson<PortalFormDetailResponse>(buildPortalPath(`/portal/forms/${detail.id}/save-draft`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const nextDetail = response?.data || null;
      setDetail(nextDetail);
      setAnswers((nextDetail?.answers as Record<string, unknown>) || {});
      setActionMessage(t('forms.draftSaved'));
      await refreshAfterAction(detail.id);
    } catch (err: any) {
      setActionError(err?.message || t('forms.saveDraftError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    if (!detail) return;
    try {
      setSubmitting(true);
      setActionError(null);
      setActionMessage(null);
      const response = await fetchJson<PortalFormDetailResponse>(buildPortalPath(`/portal/forms/${detail.id}/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const nextDetail = response?.data || null;
      setDetail(nextDetail);
      setAnswers((nextDetail?.answers as Record<string, unknown>) || {});
      setActionMessage(t('forms.submitSuccess'));
      await refreshAfterAction(detail.id);
      setTab('completed');
    } catch (err: any) {
      setActionError(err?.message || t('forms.submitError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PortalPage title={t('forms.title')} description={t('forms.description')}>
      {listError ? <ErrorBanner message={listError} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {actionMessage ? <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{actionMessage}</div> : null}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`rounded-full border px-4 py-2 text-sm font-medium transition ${badgeClass(tab === 'pending')}`} onClick={() => setTab('pending')}>
          {t('forms.pending', { count: rows.filter((item) => !isCompletedStatus(item.status)).length })}
        </button>
        <button type="button" className={`rounded-full border px-4 py-2 text-sm font-medium transition ${badgeClass(tab === 'completed')}`} onClick={() => setTab('completed')}>
          {t('forms.completed', { count: rows.filter((item) => isCompletedStatus(item.status)).length })}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <Panel title={tab === 'completed' ? t('forms.completedForms') : t('forms.pendingForms')}>
          {loadingList ? <div className="text-sm text-slate-500">{t('forms.loading')}</div> : null}
          {!loadingList && visibleRows.length === 0 ? <EmptyState message={tab === 'completed' ? t('forms.noCompleted') : t('forms.noPending')} /> : null}
          <div className="space-y-3">
            {visibleRows.map((item) => {
              const selected = item.id === selectedId;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  className={`w-full rounded-xl border p-4 text-left transition ${
                    selected ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{item.template?.name || t('forms.formFallback')}</p>
                      <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{item.template?.category || t('forms.generalCategory')}</p>
                    </div>
                    <span className={`pill ${statusPillClass(item.status)}`}>{item.status}</span>
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-between gap-3">
                      <span>{t('forms.completion')}</span>
                      <span className="font-medium text-slate-800">{item.completionPercent}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.min(100, Math.max(0, item.completionPercent || 0))}%` }} />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>{t('forms.due')}</span>
                      <span>{formatPortalDate(item.dueDate)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>{t('forms.review')}</span>
                      <span className={`pill ${statusPillClass(item.reviewStatus)}`}>{item.reviewStatus}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>

        <div className="space-y-6">
          {detailError ? <ErrorBanner message={detailError} /> : null}
          {loadingDetail ? <Panel title={t('forms.detail')}><div className="text-sm text-slate-500">{t('forms.loadingForm')}</div></Panel> : null}
          {!loadingDetail && !detail ? <EmptyState message={t('forms.selectForm')} /> : null}
          {detail ? (
            <>
              <Panel title={t('forms.summary')}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{t('forms.status')}</p>
                    <p className="mt-1"><span className={`pill ${statusPillClass(detail.status)}`}>{detail.status}</span></p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{t('forms.review')}</p>
                    <p className="mt-1"><span className={`pill ${statusPillClass(detail.reviewStatus)}`}>{detail.reviewStatus}</span></p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{t('forms.due')}</p>
                    <p className="mt-1 text-sm font-medium text-slate-800">{formatPortalDate(detail.dueDate)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{t('forms.completion')}</p>
                    <p className="mt-1 text-sm font-medium text-slate-800">{detail.completionPercent}%</p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 text-sm text-slate-600 md:grid-cols-2">
                  <div>
                    <span className="font-medium text-slate-700">{t('forms.completed_label')}</span> {formatPortalDateTime(detail.completedAt)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">{t('forms.reviewed')}</span> {formatPortalDateTime(detail.reviewedAt)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">{t('forms.signatureRequired')}</span> {detail.requiresSignature ? t('common.yes') : t('common.no')}
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">{t('forms.estimatedTime')}</span> {detail.template?.estimatedMinutes ? t('forms.minutes', { count: detail.template.estimatedMinutes }) : t('common.na')}
                  </div>
                </div>
                {detail.openMode === 'public-link' && detail.openUrl ? (
                  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    {t('forms.publicLinkFallback')}
                    <a className="ml-2 font-medium text-blue-600 hover:underline" href={detail.openUrl} target="_blank" rel="noreferrer">
                      {t('forms.openExternal')}
                    </a>
                  </div>
                ) : null}
              </Panel>

              {detail.template?.definition ? (
                <MedicalFormRenderer definition={detail.template.definition} answers={answers} readOnly={!detail.canEdit && !detail.canSubmit} onChange={(fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }))} />
              ) : (
                <EmptyState message={t('forms.definitionUnavailable')} />
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-sm text-slate-600">
                  {detail.requiresSignature ? t('forms.signatureNote') : t('forms.saveProgressNote')}
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setAnswers((detail.answers as Record<string, unknown>) || {});
                      setActionError(null);
                      setActionMessage(t('forms.changesReset'));
                    }}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    disabled={saving || submitting}
                  >
                    {t('forms.reset')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={!detail.canEdit || saving || submitting}
                  >
                    {saving ? t('forms.saving') : t('forms.saveDraft')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={!detail.canSubmit || submitting}
                  >
                    {submitting ? t('forms.submitting') : t('forms.submit')}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </PortalPage>
  );
}
